import { ApplicationStatus } from "../../../interfaces/enums";
import { ExpandLess, ExpandMore, FilterList } from "@mui/icons-material";
import { useApplicationContext } from "../../../contexts/ApplicationContext";
import { useApplicationsContext } from "../../../contexts/ApplicationsContext";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

interface CategoriesSearchSectionProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  allCollapsed: boolean;
  onToggleCollapse: () => void;
  selectedDays: number[];
  onDayFilterChange: (days: number[]) => void;
  availableDays: number[];
  showOnlyWithPresentations: boolean;
  onShowOnlyWithPresentationsChange: (value: boolean) => void;
  showOnlyWithCustomMusic: boolean;
  onShowOnlyWithCustomMusicChange: (value: boolean) => void;
}

export default function CategoriesSearchSection({
  searchTerm,
  onSearchChange,
  allCollapsed,
  onToggleCollapse,
  selectedDays,
  onDayFilterChange,
  availableDays,
  showOnlyWithPresentations,
  onShowOnlyWithPresentationsChange,
  showOnlyWithCustomMusic,
  onShowOnlyWithCustomMusicChange,
}: CategoriesSearchSectionProps) {
  const { application } = useApplicationContext();
  // Switch jest zablokowany dla statusów innych niż DRAFT
  const isSwitchDisabled = application?.status !== ApplicationStatus.DRAFT;
  const handleDayToggle = (day: number) => {
    if (selectedDays.includes(day)) {
      onDayFilterChange(selectedDays.filter((d) => d !== day));
    } else {
      onDayFilterChange([...selectedDays, day]);
    }
  };

  return (
    <Box sx={{ mb: 3, px: 2 }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Wyszukaj po kategorii, prezentacji lub tancerzu..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FilterList sx={{ color: "#6c6c6c" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#ddd",
            },
            "&:hover fieldset": {
              borderColor: "#6366f1",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6366f1",
            },
          },
        }}
      />

      {/* Filtrowanie per dzień i switch - układ responsywny */}
      <Box sx={{ mt: 2 }}>
        {/* Wiersz 1: Switche obok siebie */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.5, md: 3 },
            mb: { xs: 2, md: 2 },
          }}
        >
          {/* Filtr: Pokazuj tylko kategorie z dodaną prezentacją */}
          {!isSwitchDisabled && (
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyWithPresentations}
                  onChange={(e) => onShowOnlyWithPresentationsChange(e.target.checked)}
                  disabled={isSwitchDisabled}
                  size="small"
                />
              }
              label="Pokazuj tylko kategorie z dodaną prezentacją"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: "#2f2f2f",
                },
              }}
            />
          )}
          
          {/* TEMPORARILY DISABLED FOR PROD - Music functionality */}
          {/* Filtr: Pokazuj tylko kategorie z muzyką własną */}
          {/* <FormControlLabel
            control={
              <Switch
                checked={showOnlyWithCustomMusic}
                onChange={(e) => onShowOnlyWithCustomMusicChange(e.target.checked)}
                size="small"
              />
            }
            label="Pokazuj tylko kategorie z muzyką własną"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.8rem",
                fontWeight: 500,
                color: "#2f2f2f",
              },
            }}
          /> */}
        </Box>

        {/* Wiersz 2: Filtr po dniu - tylko dla wielodniowych zawodów */}
        {availableDays.length > 1 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#2f2f2f", fontSize: "0.8rem" }}
              >
                Filtruj po dniu:
              </Typography>
              <FormControl component="fieldset">
                <FormGroup row>
                  {availableDays
                    .sort((a, b) => a - b)
                    .map((day) => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={selectedDays.includes(day)}
                            onChange={() => handleDayToggle(day)}
                            size="small"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: "1rem",
                              },
                            }}
                          />
                        }
                        label={`Dzień ${day}`}
                        sx={{
                          mr: 2,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          },
                        }}
                      />
                    ))}
                </FormGroup>
              </FormControl>
            </Box>
          </Box>
        )}
      </Box>

      {/* Przycisk zwijania wszystkich kategorii */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onToggleCollapse}
          startIcon={allCollapsed ? <ExpandMore /> : <ExpandLess />}
          sx={{
            borderColor: "#ddd",
            color: "#6c6c6c",
            "&:hover": {
              borderColor: "#6366f1",
              color: "#6366f1",
            },
          }}
        >
          {allCollapsed ? "Rozwiń wszystkie kategorie" : "Zwiń wszystkie kategorie"}
        </Button>
      </Box>
    </Box>
  );
}
